import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, Button } from "react-bootstrap";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import api from "../../api";
import DashboardFilterBar from "../../components/dashboard/DashboardFilterBar";
import DataQualityNotice from "../../components/dashboard/DataQualityNotice";
import MetricBreakdownTable from "../../components/dashboard/MetricBreakdownTable";
import MetricCard from "../../components/dashboard/MetricCard";
import EmptyState from "../../components/dashboard/EmptyState";
import "./TeamPerformanceDashboardPage.css";

// Same registration convention already used by MetricTrendChart.jsx /
// TechnicianDashboard.js / ManagerDashboard.js elsewhere in this codebase
// -- ChartJS.register is idempotent across pages, no new charting
// approach introduced.
ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// Phase 5, §30. "Team Dashboard" — visible only with dashboards:view_team
// (reporting-manager direct reports + self). No salary/HR data anywhere.
const ACCOUNTABILITY_COLUMNS = [
  { key: "userId", label: "User ID" }, { key: "tasksAssigned", label: "Assigned" }, { key: "tasksCompleted", label: "Completed" },
  { key: "tasksOpen", label: "Open" }, { key: "tasksOverdue", label: "Overdue" }, { key: "tasksCompletedOnTime", label: "On Time" }, { key: "avgCycleHours", label: "Avg Cycle (hrs)" },
];
const CONTRIBUTION_COLUMNS = [
  { key: "userId", label: "User ID" }, { key: "tasksPerformed", label: "Performed" }, { key: "performerCompletedCount", label: "Completed" },
  { key: "workLogCount", label: "Work Logs" }, { key: "effortMinutes", label: "Effort Minutes" },
];
const FORMS_COLUMNS = [
  { key: "userId", label: "User ID" }, { key: "formsAssigned", label: "Assigned" }, { key: "formsSubmitted", label: "Submitted" },
  { key: "formsSubmittedOnTime", label: "On Time" }, { key: "avgEligibleScore", label: "Avg Score %" },
];

function withUserId(byUser) {
  return Object.entries(byUser || {}).map(([userId, v]) => ({ userId, ...v }));
}

// Local-date "YYYY-MM-DD" (matches a native <input type="date">'s own
// value format) -- mirrors the same local-getters approach already used by
// AdminTasksPage.jsx's todayDateString() to avoid a UTC-based
// toISOString() rolling the calendar date back a day in timezones east of
// UTC.
function toLocalDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const QUICK_RANGES = [
  { key: "today", label: "Today", days: 0 },
  { key: "7d", label: "Last 7 Days", days: 6 },
  { key: "month", label: "This Month", monthStart: true },
  { key: "quarter", label: "Last Quarter", months: 3 },
];

function sum(rows, key) {
  return rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
}

function average(values) {
  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length) return null;
  return finite.reduce((a, b) => a + b, 0) / finite.length;
}

// Complete Bootstrap CSS module color scale (matches the rest of this
// dashboard's badge/pill palette) reused for chart series -- not a new
// color system, just applied to a chart canvas instead of a badge.
const CHART_GREEN = "#059669";
const CHART_ORANGE = "#EA580C";
const CHART_RED = "#DC2626";
const CHART_MAROON = "#7A1236";
const CHART_MAROON_SOFT = "#C49A3A";
const CHART_GRID = "#F1F5F9";

export default function TeamPerformanceDashboardPage() {
  const [filters, setFilters] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const res = await api.get("/dashboards/team-summary", { params: filters });
      setData(res.data?.data || null);
      setLastUpdated(new Date());
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
      console.error("Fetch team-summary error:", err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const clearFilters = () => setFilters({});

  const applyQuickRange = (range) => {
    const end = new Date();
    const start = new Date();
    if (range.key === "today") {
      // start === end === today, nothing further to adjust.
    } else if (range.key === "7d") {
      start.setDate(end.getDate() - range.days);
    } else if (range.key === "month") {
      start.setDate(1);
    } else if (range.key === "quarter") {
      start.setMonth(end.getMonth() - range.months);
    }
    setFilters((f) => ({ ...f, start_date: toLocalDateString(start), end_date: toLocalDateString(end) }));
  };

  // Client-side aggregation ONLY -- every value below is derived from the
  // exact same data.breakdown.*ByUser objects the existing per-user tables
  // already render; no new API calls, no new business rules. Same pattern
  // as this page's own pre-existing `withUserId()` helper.
  const accountabilityRows = useMemo(() => withUserId(data?.breakdown?.accountabilityByUser), [data]);
  const contributionRows = useMemo(() => withUserId(data?.breakdown?.contributionByUser), [data]);
  const formsRows = useMemo(() => withUserId(data?.breakdown?.formsByUser), [data]);

  const kpiTotals = useMemo(() => {
    if (!accountabilityRows.length) return null;
    const totalAssigned = sum(accountabilityRows, "tasksAssigned");
    const totalCompleted = sum(accountabilityRows, "tasksCompleted");
    const totalOpen = sum(accountabilityRows, "tasksOpen");
    const totalOverdue = sum(accountabilityRows, "tasksOverdue");
    const avgCompletionRate = totalAssigned > 0 ? (totalCompleted / totalAssigned) * 100 : null;
    return { totalAssigned, totalCompleted, totalOpen, totalOverdue, avgCompletionRate };
  }, [accountabilityRows]);

  const avgResolutionHours = useMemo(
    () => average(accountabilityRows.map((r) => Number(r.avgCycleHours))),
    [accountabilityRows]
  );

  const contributionSummary = useMemo(() => {
    if (!contributionRows.length) return null;
    const top = [...contributionRows].sort(
      (a, b) => (Number(b.tasksPerformed) || 0) - (Number(a.tasksPerformed) || 0)
    )[0];
    const totalCompleted = sum(contributionRows, "performerCompletedCount");
    const avgWorkload = sum(contributionRows, "tasksPerformed") / contributionRows.length;
    return { top, totalCompleted, avgWorkload };
  }, [contributionRows]);

  const workloadChartData = useMemo(() => ({
    labels: accountabilityRows.map((r) => `User #${r.userId}`),
    datasets: [
      { label: "Completed", data: accountabilityRows.map((r) => Number(r.tasksCompleted) || 0), backgroundColor: CHART_GREEN, borderRadius: 3 },
      { label: "Open", data: accountabilityRows.map((r) => Number(r.tasksOpen) || 0), backgroundColor: CHART_ORANGE, borderRadius: 3 },
      { label: "Overdue", data: accountabilityRows.map((r) => Number(r.tasksOverdue) || 0), backgroundColor: CHART_RED, borderRadius: 3 },
    ],
  }), [accountabilityRows]);

  const workloadChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } } },
    scales: {
      x: { stacked: true, grid: { display: false } },
      y: { stacked: true, beginAtZero: true, grid: { color: CHART_GRID } },
    },
  }), []);

  const completionDonutData = useMemo(() => {
    if (!kpiTotals) return null;
    const remaining = Math.max(kpiTotals.totalAssigned - kpiTotals.totalCompleted, 0);
    return {
      labels: ["Completed", "Remaining"],
      datasets: [{ data: [kpiTotals.totalCompleted, remaining], backgroundColor: [CHART_GREEN, "#E2E8F0"], borderWidth: 0 }],
    };
  }, [kpiTotals]);

  const donutOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: "72%",
    plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } } },
  }), []);

  const contributionChartData = useMemo(() => {
    const totalPerformed = sum(contributionRows, "tasksPerformed");
    return {
      labels: contributionRows.map((r) => `User #${r.userId}`),
      datasets: [{
        label: "Share of team tasks performed",
        data: contributionRows.map((r) => (totalPerformed > 0 ? Math.round(((Number(r.tasksPerformed) || 0) / totalPerformed) * 1000) / 10 : 0)),
        backgroundColor: CHART_MAROON,
        borderRadius: 4,
      }],
    };
  }, [contributionRows]);

  const contributionChartOptions = useMemo(() => ({
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.x}% of team tasks performed` } },
    },
    scales: {
      x: { beginAtZero: true, max: 100, grid: { color: CHART_GRID }, ticks: { callback: (v) => `${v}%` } },
      y: { grid: { display: false } },
    },
  }), []);

  const complianceRows = useMemo(() => formsRows.map((r) => {
    const assigned = Number(r.formsAssigned) || 0;
    const onTime = Number(r.formsSubmittedOnTime) || 0;
    const onTimePct = assigned > 0 ? Math.round((onTime / assigned) * 1000) / 10 : null;
    return { ...r, onTimePct };
  }), [formsRows]);

  if (accessDenied) {
    return (
      <div className="unit-page dashboard-shell team-perf-theme">
        <div className="unit-empty">
          <div className="unit-empty-icon"><i className="fa-solid fa-lock" aria-hidden="true"></i></div>
          <h5>Access denied</h5>
          <p>You do not have permission to view the team dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="unit-page dashboard-shell team-perf-theme">
      <div className="unit-header tpd-heading-box">
        <div className="tpd-heading-content">
          <div className="tpd-heading-icon"><i className="fa-solid fa-people-group" aria-hidden="true"></i></div>
          <div>
            <div className="unit-page-label">Performance</div>
            <h2>Team Dashboard</h2>
            <p>Workload, completion, TAT compliance, contribution, form and UAT summary for your direct reports.</p>
          </div>
        </div>
        <div className="unit-header-actions tpd-header-actions">
          <div className="perf-team-size-chip tpd-team-size-chip">
            <i className="fa-solid fa-users" aria-hidden="true"></i>
            Team size: <strong>{data?.teamSize ?? 0}</strong>
          </div>
          {lastUpdated && (
            <div className="tpd-last-updated">
              <i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
              Updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>
      </div>

      <div className="tpd-filter-card">
        <div className="tpd-quick-filters">
          {QUICK_RANGES.map((r) => (
            <Button key={r.key} size="sm" className="tpd-quick-filter-btn" onClick={() => applyQuickRange(r)}>
              {r.label}
            </Button>
          ))}
          <span className="tpd-quick-filter-custom">
            <i className="fa-solid fa-sliders" aria-hidden="true"></i>
            Custom range below
          </span>
        </div>
        <DashboardFilterBar filters={filters} onChange={setFilters} showDepartment={false} showProject={false} />
      </div>

      {loading ? (
        <div className="perf-skeleton-stack">
          {[0, 1, 2].map((i) => <div key={i} className="perf-skeleton-block" />)}
        </div>
      ) : (
        <>
          <DataQualityNotice warnings={data?.warnings} />

          {/* ---------- KPI summary row ---------- */}
          {kpiTotals ? (
            <div className="tpd-kpi-row">
              <MetricCard label="Total Tasks" value={kpiTotals.totalAssigned} direction="INFORMATIONAL_ONLY" icon="fa-list-check" subtext="Assigned across the team" />
              <MetricCard label="Completed Tasks" value={kpiTotals.totalCompleted} direction="HIGHER_IS_BETTER" icon="fa-circle-check" subtext="This period" />
              <MetricCard label="Pending Tasks" value={kpiTotals.totalOpen} direction="INFORMATIONAL_ONLY" accent="warning" icon="fa-folder-open" subtext="Currently open" />
              <MetricCard label="Overdue Tasks" value={kpiTotals.totalOverdue} direction="LOWER_IS_BETTER" icon="fa-triangle-exclamation" subtext="Past due date" />
              <MetricCard label="Avg Completion Rate" value={kpiTotals.avgCompletionRate} isPercentage direction="HIGHER_IS_BETTER" icon="fa-gauge-high" subtext="Completed / assigned" />
            </div>
          ) : (
            <EmptyState
              icon="fa-chart-simple"
              title="No performance data available"
              description="Data will appear once team activity is recorded for the selected period."
              onClearFilters={clearFilters}
            />
          )}

          {/* ---------- Workload & Completion ---------- */}
          <Card className="perf-section-card tpd-workload-card"><Card.Body>
            <div className="tpd-section-header">
              <div className="tpd-section-header-left">
                <div className="tpd-section-icon"><i className="fa-solid fa-list-check" aria-hidden="true"></i></div>
                <div>
                  <h6 className="perf-section-title tpd-section-title">Workload &amp; Completion</h6>
                  <p className="tpd-section-desc">Per-member task breakdown and team completion rate for the selected period.</p>
                </div>
              </div>
            </div>

            {accountabilityRows.length > 0 ? (
              <>
                <div className="tpd-chart-row">
                  <div className="tpd-chart-main">
                    <div className="tpd-chart-canvas"><Bar data={workloadChartData} options={workloadChartOptions} /></div>
                  </div>
                  <div className="tpd-chart-side">
                    <div className="tpd-donut-canvas">{completionDonutData && <Doughnut data={completionDonutData} options={donutOptions} />}</div>
                    <div className="tpd-donut-stat">
                      <strong>{kpiTotals?.avgCompletionRate != null ? `${Math.round(kpiTotals.avgCompletionRate)}%` : "—"}</strong>
                      <span>Completed</span>
                    </div>
                  </div>
                </div>
                <MetricBreakdownTable rows={accountabilityRows} columns={ACCOUNTABILITY_COLUMNS} />
              </>
            ) : (
              <EmptyState icon="fa-chart-column" title="No activity available" description="Select another period." maxHeight="220px" />
            )}
          </Card.Body></Card>

          {/* ---------- Contribution ---------- */}
          <Card className="perf-section-card"><Card.Body>
            <div className="tpd-section-header">
              <div className="tpd-section-header-left">
                <div className="tpd-section-icon"><i className="fa-solid fa-chart-line" aria-hidden="true"></i></div>
                <div>
                  <h6 className="perf-section-title tpd-section-title">Contribution</h6>
                  <p className="tpd-section-desc">Share of team tasks performed by each member, and headline contribution stats.</p>
                </div>
              </div>
            </div>

            {contributionRows.length > 0 ? (
              <>
                <div className="tpd-contribution-row">
                  <div className="tpd-chart-main">
                    <div className="tpd-hbar-canvas" style={{ height: Math.max(contributionRows.length * 34, 120) }}>
                      <Bar data={contributionChartData} options={contributionChartOptions} />
                    </div>
                  </div>
                  <div className="tpd-contribution-summary">
                    <div className="tpd-summary-stat">
                      <span>Top Contributor</span>
                      <strong>{contributionSummary?.top ? `User #${contributionSummary.top.userId}` : "—"}</strong>
                    </div>
                    <div className="tpd-summary-stat">
                      <span>Average Workload</span>
                      <strong>{contributionSummary ? Math.round(contributionSummary.avgWorkload * 10) / 10 : "—"}</strong>
                    </div>
                    <div className="tpd-summary-stat">
                      <span>Tasks Completed</span>
                      <strong>{contributionSummary ? contributionSummary.totalCompleted : "—"}</strong>
                    </div>
                    <div className="tpd-summary-stat">
                      <span>Avg Resolution Time</span>
                      <strong>{avgResolutionHours != null ? `${Math.round(avgResolutionHours * 10) / 10}h` : "Not Available"}</strong>
                    </div>
                  </div>
                </div>
                <MetricBreakdownTable rows={contributionRows} columns={CONTRIBUTION_COLUMNS} />
              </>
            ) : (
              <EmptyState icon="fa-chart-column" title="No activity available" description="Select another period." maxHeight="220px" />
            )}
          </Card.Body></Card>

          {/* ---------- Form Compliance ---------- */}
          <Card className="perf-section-card"><Card.Body>
            <div className="tpd-section-header">
              <div className="tpd-section-header-left">
                <div className="tpd-section-icon"><i className="fa-solid fa-file-lines" aria-hidden="true"></i></div>
                <div>
                  <h6 className="perf-section-title tpd-section-title">Form Compliance</h6>
                  <p className="tpd-section-desc">On-time submission rate per member, from forms assigned in the selected period.</p>
                </div>
              </div>
            </div>

            {complianceRows.length > 0 ? (
              <div className="table-responsive tpd-compliance-table-wrap">
                <table className="tpd-compliance-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th className="text-center">Assigned Forms</th>
                      <th className="text-center">Submitted</th>
                      <th className="text-center">Pending</th>
                      <th>On Time %</th>
                      <th className="text-center">Average Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complianceRows.map((row) => {
                      const pending = (Number(row.formsAssigned) || 0) - (Number(row.formsSubmitted) || 0);
                      const tone = row.onTimePct == null ? "na" : row.onTimePct >= 90 ? "good" : row.onTimePct >= 70 ? "warn" : "bad";
                      return (
                        <tr key={row.userId}>
                          <td>User #{row.userId}</td>
                          <td className="text-center">{row.formsAssigned ?? <span className="perf-not-available">Not Available</span>}</td>
                          <td className="text-center">{row.formsSubmitted ?? <span className="perf-not-available">Not Available</span>}</td>
                          <td className="text-center">{Number.isFinite(pending) ? Math.max(pending, 0) : <span className="perf-not-available">Not Available</span>}</td>
                          <td>
                            {row.onTimePct != null ? (
                              <div className="tpd-progress-wrap">
                                <div className="tpd-progress-track">
                                  <div className={`tpd-progress-fill tpd-progress-${tone}`} style={{ width: `${Math.min(row.onTimePct, 100)}%` }} />
                                </div>
                                <span className={`tpd-progress-label tpd-progress-label-${tone}`}>{row.onTimePct}%</span>
                              </div>
                            ) : (
                              <span className="perf-not-available">Not Available</span>
                            )}
                          </td>
                          <td className="text-center">{row.avgEligibleScore != null ? `${row.avgEligibleScore}%` : <span className="perf-not-available">Not Available</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState icon="fa-file-circle-xmark" title="No performance data available" description="Data will appear once team activity is recorded." onClearFilters={clearFilters} />
            )}
          </Card.Body></Card>
        </>
      )}
    </div>
  );
}
